import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from "react";
import type {
  Episode,
  EpisodeView,
  Id,
  RestoreArchiveMode,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  countProductionScenes,
  episodeStatusProgress,
  formatDuration,
  getEpisodeScenes,
  listEpisodes,
  parseItemNumber,
  productionStatusProgress,
  productionTotalDuration,
  sceneStatusProgress,
  totalSceneDuration,
  VAULT_RETENTION_DAYS,
} from "@supermanus/document-model";
import { InlineEditable } from "./InlineEditable";
import { ProductionVaultPanel } from "./ProductionVaultPanel";
import { ProgressRing, StatusPointsStat } from "./StatusProgressBar";
import {
  HELP_TIPS,
  loadHelpTipIndex,
  saveHelpTipIndex,
  type HelpSectionId,
} from "./helpContent";
import type { EditorSettings } from "./editorSettings";
import { reorderDropClass } from "./useSceneReorder";
import { reorderByBefore, arrayMoveIds, usePointerReorder } from "./usePointerReorder";

export interface ProductionDashboardProps {
  document: ScriptDocument;
  readOnly?: boolean;
  settings: EditorSettings;
  displayName: string;
  onRenameDisplayName: (name: string) => void;
  onRenameProduction: (title: string) => void;
  onGoHome?: () => void;
  onOpenHelp: (section?: HelpSectionId) => void;
  onOpenSettings: () => void;
  onOpenEpisode: (episodeId: Id, view?: EpisodeView, sceneId?: Id) => void;
  /** Open episode in tilrettelæggermanus (personal forks overlay). */
  onOpenDirectorEpisode?: (episodeId: Id) => void;
  onAddSeason?: () => void;
  onAddEpisode?: (seasonId?: Id | null) => void;
  onDeleteEpisode?: (episodeId: Id) => void;
  onRenameEpisode?: (episodeId: Id, title: string) => void;
  onChangeEpisodeNumber?: (episodeId: Id, number: string) => void;
  onDuplicateEpisode?: (episodeId: Id) => void;
  /** Download a complete episode manuscript backup file. */
  onExportEpisodeBackup?: (episodeId: Id) => void;
  /** Pick a backup file and insert it as a new episode. */
  onImportEpisodeBackup?: () => void;
  onCopyEpisodeToProduction?: (episodeId: Id, destProductionId: Id) => void;
  onMoveEpisodeToProduction?: (episodeId: Id, destProductionId: Id) => void;
  /** Move episode to another season in this production. */
  onMoveEpisodeToSeason?: (episodeId: Id, destSeasonId: Id) => void;
  /** Other productions available as copy/move targets (excludes current). */
  otherProductions?: { id: Id; title: string }[];
  onReorderEpisodes?: (orderedIds: Id[]) => void;
  onCopyEpisodeLink?: (episodeId: Id) => void;
  syncStatus?: string;
  licenseStatus?: string;
  onRestoreTrashEpisode?: (episodeId: Id) => void;
  onPurgeTrashEpisode?: (episodeId: Id) => void;
  onRestoreTrashScene?: (sceneId: Id) => void;
  onPurgeTrashScene?: (sceneId: Id) => void;
  onEmptyTrash?: () => void;
  onRestoreArchiveScene?: (
    archivedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
  onPurgeArchiveScene?: (archivedSceneId: Id) => void;
  onPurgeArchiveEpisode?: (episodeId: Id) => void;
  onEmptyArchive?: () => void;
  onInsertSubmittedIntoProgram?: (
    submittedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
}

function episodeDuration(doc: ScriptDocument, episodeId: Id): number {
  return totalSceneDuration(getEpisodeScenes(doc, episodeId));
}

function SortableEpisodeMatrix({
  episodes,
  readOnly,
  onReorderEpisodes,
  children,
}: {
  episodes: Episode[];
  readOnly?: boolean;
  onReorderEpisodes?: (orderedIds: Id[]) => void;
  children: (ctx: {
    beginDrag: (id: Id, e: ReactPointerEvent<HTMLElement>) => void;
    dropClass: (id: Id, isLast: boolean) => string;
    dragId: Id | null;
    showGrip: boolean;
  }) => ReactNode;
}) {
  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    readOnly: Boolean(readOnly) || !onReorderEpisodes || episodes.length < 2,
    axis: "y",
    onMove: (id, beforeId) => {
      if (!onReorderEpisodes) return;
      onReorderEpisodes(
        reorderByBefore(episodes, id, beforeId).map((ep) => ep.id),
      );
    },
  });
  const showGrip =
    !readOnly && Boolean(onReorderEpisodes) && episodes.length > 1;
  return (
    <div
      className={`sm-dash-ep-matrix${dragId ? " is-reordering" : ""}`}
      ref={listRef as Ref<HTMLDivElement>}
    >
      {children({
        beginDrag,
        dropClass: (id, isLast) =>
          reorderDropClass(dragId, dropBeforeId, id, isLast),
        dragId,
        showGrip,
      })}
    </div>
  );
}

export function ProductionDashboard({
  document,
  readOnly,
  settings,
  displayName,
  onRenameDisplayName,
  onRenameProduction,
  onGoHome,
  onOpenHelp,
  onOpenSettings,
  onOpenEpisode,
  onOpenDirectorEpisode,
  onAddSeason,
  onAddEpisode,
  onDeleteEpisode,
  onRenameEpisode,
  onChangeEpisodeNumber,
  onDuplicateEpisode,
  onExportEpisodeBackup,
  onImportEpisodeBackup,
  onCopyEpisodeToProduction,
  onMoveEpisodeToProduction,
  onMoveEpisodeToSeason,
  otherProductions,
  onReorderEpisodes,
  onCopyEpisodeLink,
  syncStatus = "offline",
  licenseStatus = "ok",
  onRestoreTrashEpisode,
  onPurgeTrashEpisode,
  onRestoreTrashScene,
  onPurgeTrashScene,
  onEmptyTrash,
  onRestoreArchiveScene,
  onPurgeArchiveScene,
  onPurgeArchiveEpisode,
  onEmptyArchive,
  onInsertSubmittedIntoProgram,
}: ProductionDashboardProps) {
  const episodes = listEpisodes(document);
  const useSeasons = Boolean(document.production.useSeasons);
  const seasons = document.production.seasonIds
    .map((id) => document.seasons[id])
    .filter((s): s is NonNullable<typeof s> => Boolean(s));
  const prodProgress = productionStatusProgress(document);
  const sceneCount = countProductionScenes(document);
  const totalDur = productionTotalDuration(document);
  const seasonCount = useSeasons ? seasons.length : 0;

  const durFmt = {
    showHours: settings.durationShowHours,
    showFrames: settings.durationShowFrames,
    fps: settings.durationFps,
  };

  const [tipIndex, setTipIndex] = useState(() => loadHelpTipIndex());
  const tip = HELP_TIPS[tipIndex % HELP_TIPS.length]!;

  const [openSeasonIds, setOpenSeasonIds] = useState<Set<Id>>(
    () => new Set(seasons.map((s) => s.id)),
  );
  const [openStatusEpisodeIds, setOpenStatusEpisodeIds] = useState<Set<Id>>(
    new Set(),
  );
  const [handleMenuId, setHandleMenuId] = useState<Id | null>(null);
  const [renamingId, setRenamingId] = useState<Id | null>(null);
  const [transferPick, setTransferPick] = useState<{
    episodeId: Id;
    mode: "copy" | "move";
  } | null>(null);
  const [seasonMovePick, setSeasonMovePick] = useState<Id | null>(null);
  const handleMenuRef = useRef<HTMLDivElement | null>(null);

  const groups = useMemo(() => {
    if (useSeasons && seasons.length > 0) {
      return seasons.map((season) => ({
        key: season.id,
        title: season.title,
        seasonId: season.id as Id | null,
        episodes: season.episodeIds
          .map((id) => document.episodes[id])
          .filter((e): e is Episode => Boolean(e)),
      }));
    }
    return [
      {
        key: "all",
        title: "Episoder",
        seasonId: null as Id | null,
        episodes,
      },
    ];
  }, [document.episodes, episodes, seasons, useSeasons]);

  function toggleSet(set: Set<Id>, id: Id): Set<Id> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  const episodeCount = episodes.length;
  const canDeleteEpisode = !readOnly && Boolean(onDeleteEpisode) && episodeCount > 1;

  const knownSeasonIds = useRef<Set<Id>>(
    new Set(seasons.map((s) => s.id)),
  );

  useEffect(() => {
    const known = knownSeasonIds.current;
    const added: Id[] = [];
    for (const s of seasons) {
      if (!known.has(s.id)) {
        known.add(s.id);
        added.push(s.id);
      }
    }
    if (added.length === 0) return;
    setOpenSeasonIds((prev) => {
      const next = new Set(prev);
      for (const id of added) next.add(id);
      return next;
    });
  }, [seasons]);

  useEffect(() => {
    if (!handleMenuId) return;
    const onDoc = (e: MouseEvent) => {
      if (handleMenuRef.current?.contains(e.target as Node)) return;
      setHandleMenuId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHandleMenuId(null);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [handleMenuId]);

  function nextTip() {
    const next = (tipIndex + 1) % HELP_TIPS.length;
    setTipIndex(next);
    saveHelpTipIndex(next);
  }

  function handleAddSeason() {
    onAddSeason?.();
  }

  function handleAddEpisode(seasonId?: Id | null) {
    if (seasonId) {
      setOpenSeasonIds((prev) => {
        const next = new Set(prev);
        next.add(seasonId);
        return next;
      });
    }
    onAddEpisode?.(seasonId);
  }

  function handleDeleteEpisode(ep: Episode) {
    if (!canDeleteEpisode) return;
    const name = ep.title.trim() || "Unavngiven episode";
    if (
      !window.confirm(
        `Flyt episoden «${name}» til papirkurven? Du kan gendanne den inden for ${VAULT_RETENTION_DAYS} dage — derefter slettes den automatisk.`,
      )
    ) {
      return;
    }
    onDeleteEpisode?.(ep.id);
  }

  function handleDuplicateEpisode(ep: Episode) {
    setHandleMenuId(null);
    onDuplicateEpisode?.(ep.id);
  }

  function handleStartRename(ep: Episode) {
    setHandleMenuId(null);
    setRenamingId(ep.id);
  }

  function handleShiftEpisode(ep: Episode, dir: -1 | 1, groupEpisodes: Episode[]) {
    if (!onReorderEpisodes) return;
    const ids = groupEpisodes.map((item) => item.id);
    const from = ids.indexOf(ep.id);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= ids.length) return;
    setHandleMenuId(null);
    onReorderEpisodes(arrayMoveIds(ids, from, to));
  }

  return (
    <div className="sm-dash">
      <div className="sm-toolbar sm-dash-toolbar">
        {onGoHome ? (
          <button
            type="button"
            className="sm-home-link"
            onClick={onGoHome}
            title="Tilbage til alle produktioner"
          >
            ← Produktioner
          </button>
        ) : null}
        <InlineEditable
          as="h1"
          className="sm-dash-title"
          value={document.production.title}
          onChange={onRenameProduction}
          readOnly={readOnly}
          placeholder="Unavngiven produktion"
        />
        <div className="sm-toolbar-actions">
          <button type="button" onClick={() => onOpenHelp()}>
            Hjælp
          </button>
          <button type="button" onClick={onOpenSettings}>
            Indstillinger
          </button>
        </div>
        <span className="sm-status sm-user-status">
          <InlineEditable
            as="span"
            className="sm-user-name"
            value={displayName}
            onChange={onRenameDisplayName}
            placeholder="Dit navn"
            title="Klik for at ændre dit navn"
          />
          <span>
            {" "}
            · {syncStatus}
            {licenseStatus !== "ok" ? ` · licens: ${licenseStatus}` : ""}
          </span>
        </span>
      </div>

      <div className="sm-dash-hero">
        <ProgressRing pct={prodProgress.pct} />
        <div className="sm-dash-stats">
          <StatusPointsStat
            completed={prodProgress.completed}
            total={prodProgress.total}
          />
          {useSeasons ? (
            <div className="sm-dash-stat">
              <strong>{seasonCount}</strong>
              <span>{seasonCount === 1 ? "sæson" : "sæsoner"}</span>
            </div>
          ) : null}
          <div className="sm-dash-stat">
            <strong>{episodes.length}</strong>
            <span>{episodes.length === 1 ? "episode" : "episoder"}</span>
          </div>
          <div className="sm-dash-stat">
            <strong>{sceneCount}</strong>
            <span>{sceneCount === 1 ? "scene" : "scener"}</span>
          </div>
          <div className="sm-dash-stat">
            <strong>{formatDuration(totalDur, durFmt)}</strong>
            <span>samlet varighed</span>
          </div>
        </div>
      </div>

      <div className="sm-dash-grid">
        <section className="sm-dash-card">
          <h2 className="sm-sr-only">Episoder</h2>
          {!readOnly && (onAddSeason || onAddEpisode) ? (
            <div className="sm-dash-card-head">
              <div className="sm-dash-create">
                {onAddSeason ? (
                  <button type="button" onClick={handleAddSeason}>
                    Ny sæson
                  </button>
                ) : null}
                {onAddEpisode ? (
                  <button
                    type="button"
                    onClick={() =>
                      handleAddEpisode(
                        useSeasons ? (seasons.at(-1)?.id ?? null) : null,
                      )
                    }
                  >
                    Ny episode
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
          {episodes.length === 0 && seasons.length === 0 ? (
            <p className="sm-dash-empty">
              Opret en sæson eller en episode for at komme i gang.
            </p>
          ) : null}
          <ul className="sm-dash-ep-list">
            {groups.map((group) => {
              const seasonOpen =
                !group.seasonId || openSeasonIds.has(group.seasonId);
              return (
                <li key={group.key} className="sm-dash-ep-group">
                  {group.seasonId ? (
                    <div className="sm-dash-season-row">
                      <button
                        type="button"
                        className="sm-dash-tree-toggle"
                        aria-expanded={seasonOpen}
                        onClick={() =>
                          setOpenSeasonIds((prev) =>
                            toggleSet(prev, group.seasonId!),
                          )
                        }
                      >
                        {seasonOpen ? "▾" : "▸"} {group.title}
                      </button>
                      {!readOnly && onAddEpisode ? (
                        <button
                          type="button"
                          className="sm-dash-link"
                          onClick={() => handleAddEpisode(group.seasonId)}
                        >
                          Ny episode
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {seasonOpen ? (
                    <SortableEpisodeMatrix
                      episodes={group.episodes}
                      readOnly={readOnly}
                      onReorderEpisodes={onReorderEpisodes}
                    >
                      {({ beginDrag, dropClass, dragId, showGrip }) =>
                        group.episodes.map((ep, i) => {
                        const scenes = getEpisodeScenes(document, ep.id);
                        const epPct = episodeStatusProgress(document, ep.id);
                        const title = ep.title.trim() || "Unavngiven episode";
                        const menuOpen = handleMenuId === ep.id;
                        const isLast = i === group.episodes.length - 1;
                        return (
                          <article
                            key={ep.id}
                            data-step-id={ep.id}
                            className={`sm-dash-ep-card${dragId === ep.id ? " is-dragging" : ""}${dropClass(ep.id, isLast)}`}
                          >
                            <div className="sm-dash-ep-row">
                              {showGrip ? (
                                <button
                                  type="button"
                                  className="sm-scene-grip"
                                  aria-label="Flyt episode"
                                  title="Træk for at ændre rækkefølge"
                                  onPointerDown={(e) => beginDrag(ep.id, e)}
                                >
                                  ⋮⋮
                                </button>
                              ) : null}
                              <div className="sm-dash-ep-head">
                                {renamingId === ep.id && onRenameEpisode ? (
                                  <span className="sm-dash-ep-rename">
                                    <EpisodeNumberPrefix
                                      episode={ep}
                                      show={settings.showEpisodeNumbers}
                                      readOnly={readOnly}
                                      onNumberChange={
                                        onChangeEpisodeNumber
                                          ? (number) =>
                                              onChangeEpisodeNumber(ep.id, number)
                                          : undefined
                                      }
                                    />
                                    <EpisodeRenameField
                                      value={ep.title}
                                      placeholder="Unavngiven episode"
                                      onChange={(next) =>
                                        onRenameEpisode(ep.id, next)
                                      }
                                      onDone={() => setRenamingId(null)}
                                    />
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="sm-dash-ep-open"
                                    title="Åbn episode"
                                    onClick={() =>
                                      onOpenEpisode(ep.id, "timing")
                                    }
                                  >
                                    <EpisodeNumberPrefix
                                      episode={ep}
                                      show={settings.showEpisodeNumbers}
                                    />
                                    {title}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="sm-dash-ep-meta-hit"
                                  title="Åbn episode"
                                  onClick={() =>
                                    onOpenEpisode(ep.id, "timing")
                                  }
                                >
                                  <span className="sm-dash-ep-meta">
                                    {scenes.length}{" "}
                                    {scenes.length === 1 ? "scene" : "scener"} ·{" "}
                                    {formatDuration(
                                      episodeDuration(document, ep.id),
                                      durFmt,
                                    )}{" "}
                                    · {epPct.pct}%
                                  </span>
                                  <span
                                    className="sm-status-progress"
                                    aria-hidden
                                  >
                                    <span style={{ width: `${epPct.pct}%` }} />
                                  </span>
                                </button>
                              </div>
                              {!readOnly ? (
                                <div
                                  className="sm-dash-ep-handle"
                                  ref={menuOpen ? handleMenuRef : undefined}
                                >
                                  {onOpenDirectorEpisode ? (
                                    <button
                                      type="button"
                                      title="Åbn dit tilrettelæggermanus for episoden"
                                      onClick={() =>
                                        onOpenDirectorEpisode(ep.id)
                                      }
                                    >
                                      Tilrettelæggermanus
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className={`sm-dash-ep-gear${menuOpen ? " is-active" : ""}`}
                                    title="Håndtér episode"
                                    aria-label="Håndtér episode"
                                    aria-haspopup="menu"
                                    aria-expanded={menuOpen}
                                    onClick={() =>
                                      setHandleMenuId((cur) =>
                                        cur === ep.id ? null : ep.id,
                                      )
                                    }
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      width="16"
                                      height="16"
                                      aria-hidden="true"
                                      focusable="false"
                                    >
                                      <path
                                        fill="currentColor"
                                        d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.07 7.07 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.59.24-1.13.55-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 8.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.83 14.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.23.4.32.6.22l2.39-.96c.5.39 1.04.7 1.63.94l.36 2.54c.05.24.25.42.49.42h3.8c.24 0 .44-.18.49-.42l.36-2.54c.59-.24 1.13-.55 1.63-.94l2.39.96c.23.1.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z"
                                      />
                                    </svg>
                                  </button>
                                  {menuOpen ? (
                                    <div
                                      className="sm-ctx-menu sm-dash-ep-menu"
                                      role="menu"
                                    >
                                      {onRenameEpisode ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() => handleStartRename(ep)}
                                        >
                                          {settings.showEpisodeNumbers &&
                                          onChangeEpisodeNumber
                                            ? "Omdøb / ændr nummer"
                                            : "Omdøb"}
                                        </button>
                                      ) : null}
                                      {onReorderEpisodes && group.episodes.length > 1 ? (
                                        <>
                                          <button
                                            type="button"
                                            className="sm-ctx-menu-item"
                                            role="menuitem"
                                            disabled={i === 0}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleShiftEpisode(ep, -1, group.episodes);
                                            }}
                                          >
                                            Flyt op
                                          </button>
                                          <button
                                            type="button"
                                            className="sm-ctx-menu-item"
                                            role="menuitem"
                                            disabled={i === group.episodes.length - 1}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleShiftEpisode(ep, 1, group.episodes);
                                            }}
                                          >
                                            Flyt ned
                                          </button>
                                        </>
                                      ) : null}
                                      {onDuplicateEpisode ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() =>
                                            handleDuplicateEpisode(ep)
                                          }
                                        >
                                          Duplikér
                                        </button>
                                      ) : null}
                                      {onExportEpisodeBackup ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          title="Gem komplet episodemanus (inkl. noter) som fil"
                                          onClick={() => {
                                            setHandleMenuId(null);
                                            onExportEpisodeBackup(ep.id);
                                          }}
                                        >
                                          Gem sikkerhedskopi…
                                        </button>
                                      ) : null}
                                      {onMoveEpisodeToSeason &&
                                      useSeasons &&
                                      seasons.filter((s) => s.id !== ep.seasonId)
                                        .length > 0 ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() => {
                                            setHandleMenuId(null);
                                            setSeasonMovePick(ep.id);
                                          }}
                                        >
                                          Flyt til sæson…
                                        </button>
                                      ) : null}
                                      {onCopyEpisodeToProduction &&
                                      otherProductions &&
                                      otherProductions.length > 0 ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() => {
                                            setHandleMenuId(null);
                                            setTransferPick({
                                              episodeId: ep.id,
                                              mode: "copy",
                                            });
                                          }}
                                        >
                                          Kopiér til produktion…
                                        </button>
                                      ) : null}
                                      {onMoveEpisodeToProduction &&
                                      otherProductions &&
                                      otherProductions.length > 0 ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() => {
                                            setHandleMenuId(null);
                                            setTransferPick({
                                              episodeId: ep.id,
                                              mode: "move",
                                            });
                                          }}
                                        >
                                          Flyt til produktion…
                                        </button>
                                      ) : null}
                                      {onCopyEpisodeLink ? (
                                        <button
                                          type="button"
                                          className="sm-ctx-menu-item"
                                          role="menuitem"
                                          onClick={() => {
                                            setHandleMenuId(null);
                                            onCopyEpisodeLink(ep.id);
                                          }}
                                        >
                                          Kopiér link til episode
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="sm-ctx-menu-item sm-ctx-menu-item-danger"
                                        role="menuitem"
                                        disabled={!canDeleteEpisode}
                                        onClick={() => {
                                          setHandleMenuId(null);
                                          handleDeleteEpisode(ep);
                                        }}
                                      >
                                        Slet
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </article>
                        );
                      })
                      }
                    </SortableEpisodeMatrix>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {!readOnly && onImportEpisodeBackup ? (
            <div className="sm-dash-ep-foot">
              <button
                type="button"
                title="Indsæt et komplet episodemanus fra en sikkerhedskopifil"
                onClick={() => onImportEpisodeBackup()}
              >
                Indsæt fra fil…
              </button>
            </div>
          ) : null}
        </section>

        <section className="sm-dash-card">
          <h2>Status</h2>
          {episodes.length === 0 ? (
            <p className="sm-dash-empty">
              Opret en episode for at se statuspunkter.
            </p>
          ) : (
            <>
              <article className="sm-status-scene sm-dash-status-static">
                <div className="sm-status-scene-row">
                  <div className="sm-status-scene-head">
                    <span>Hele produktionen</span>
                    <span className="sm-status-pct">
                      {prodProgress.completed}/{prodProgress.total} ·{" "}
                      {prodProgress.pct}%
                    </span>
                  </div>
                </div>
                <div className="sm-status-progress" aria-hidden>
                  <span style={{ width: `${prodProgress.pct}%` }} />
                </div>
              </article>
              <section
                className="sm-status-matrix sm-dash-status-matrix"
                aria-label="Episodestatus"
              >
                {episodes.map((ep) => {
                  const epOpen = openStatusEpisodeIds.has(ep.id);
                  const epPct = episodeStatusProgress(document, ep.id);
                  const scenes = getEpisodeScenes(document, ep.id);
                  const title = ep.title.trim() || "Unavngiven episode";
                  return (
                    <article
                      key={ep.id}
                      className={`sm-status-scene${epOpen ? " is-open" : ""}`}
                      onClick={() =>
                        setOpenStatusEpisodeIds((prev) =>
                          toggleSet(prev, ep.id),
                        )
                      }
                    >
                      <div className="sm-status-scene-row">
                        <button
                          type="button"
                          className="sm-status-expand"
                          aria-expanded={epOpen}
                          aria-label={
                            epOpen ? "Skjul scener" : "Vis scener"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenStatusEpisodeIds((prev) =>
                              toggleSet(prev, ep.id),
                            );
                          }}
                        >
                          {epOpen ? "▾" : "▸"}
                        </button>
                        <div className="sm-status-scene-head">
                          <span>{title}</span>
                          <span className="sm-status-pct">
                            {epPct.completed}/{epPct.total} · {epPct.pct}%
                          </span>
                        </div>
                        <button
                          type="button"
                          className="sm-dash-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenEpisode(ep.id, "status");
                          }}
                        >
                          Åbn statuspunkter
                        </button>
                      </div>
                      <div className="sm-status-progress" aria-hidden>
                        <span style={{ width: `${epPct.pct}%` }} />
                      </div>
                      {epOpen ? (
                        <div
                          className="sm-status-scene-body sm-dash-status-scenes"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {scenes.length === 0 ? (
                            <p className="sm-status-hint">
                              Ingen scener i episoden.
                            </p>
                          ) : (
                            scenes.map((scene) => {
                              const sp = sceneStatusProgress(
                                scene,
                                document.production.statusSteps,
                              );
                              const heading =
                                scene.heading?.trim() ||
                                scene.title ||
                                "Unavngiven";
                              return (
                                <article
                                  key={scene.id}
                                  className="sm-status-scene sm-dash-status-static"
                                >
                                  <div className="sm-status-scene-row">
                                    <div className="sm-status-scene-head">
                                      <span>
                                        Scene {scene.number}: {heading}
                                      </span>
                                      <span className="sm-status-pct">
                                        {sp.completed}/{sp.total} · {sp.pct}%
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className="sm-status-progress"
                                    aria-hidden
                                  >
                                    <span style={{ width: `${sp.pct}%` }} />
                                  </div>
                                </article>
                              );
                            })
                          )}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </section>
            </>
          )}
        </section>

        <ProductionVaultPanel
          document={document}
          showEpisodeNumbers={settings.showEpisodeNumbers}
          readOnly={readOnly}
          onOpenScene={(sceneId) => {
            const scene = document.scenes[sceneId];
            if (!scene) return;
            onOpenEpisode(scene.episodeId, "script", sceneId);
          }}
          onRestoreTrashEpisode={onRestoreTrashEpisode}
          onPurgeTrashEpisode={onPurgeTrashEpisode}
          onRestoreTrashScene={onRestoreTrashScene}
          onPurgeTrashScene={onPurgeTrashScene}
          onEmptyTrash={onEmptyTrash}
          onRestoreArchiveScene={onRestoreArchiveScene}
          onPurgeArchiveScene={onPurgeArchiveScene}
          onPurgeArchiveEpisode={onPurgeArchiveEpisode}
          onEmptyArchive={onEmptyArchive}
          onInsertSubmittedIntoProgram={onInsertSubmittedIntoProgram}
        />

        <section className="sm-dash-card sm-dash-tip">
          <h2>Tip</h2>
          <p>{tip.text}</p>
          <div className="sm-dash-tip-actions">
            <button
              type="button"
              className="sm-dash-link"
              onClick={() => onOpenHelp(tip.sectionId)}
            >
              Læs mere
            </button>
            <button type="button" onClick={nextTip}>
              Næste tip
            </button>
          </div>
        </section>
      </div>
      {transferPick && otherProductions && otherProductions.length > 0 ? (
        <div
          className="sm-modal-backdrop"
          role="presentation"
          onClick={() => setTransferPick(null)}
        >
          <div
            className="sm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sm-ep-transfer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="sm-modal-head">
              <h2 id="sm-ep-transfer-title">
                {transferPick.mode === "move"
                  ? "Flyt episode til produktion"
                  : "Kopiér episode til produktion"}
              </h2>
              <button type="button" onClick={() => setTransferPick(null)}>
                Luk
              </button>
            </header>
            <div className="sm-settings-body">
              <p>
                Vælg destination.{" "}
                {transferPick.mode === "move"
                  ? "Episoden fjernes fra denne produktion efter kopiering."
                  : "En kopi oprettes i den valgte produktion."}
              </p>
              <ul className="sm-ep-transfer-list">
                {otherProductions.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="sm-ctx-menu-item"
                      onClick={() => {
                        const { episodeId, mode } = transferPick;
                        setTransferPick(null);
                        if (mode === "move") {
                          onMoveEpisodeToProduction?.(episodeId, p.id);
                        } else {
                          onCopyEpisodeToProduction?.(episodeId, p.id);
                        }
                      }}
                    >
                      {p.title.trim() || "Unavngiven produktion"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
      {seasonMovePick && onMoveEpisodeToSeason
        ? (() => {
            const ep = document.episodes[seasonMovePick];
            const targets = seasons.filter((s) => s.id !== ep?.seasonId);
            if (!ep || targets.length === 0) return null;
            const epTitle = ep.title.trim() || "Unavngiven episode";
            return (
              <div
                className="sm-modal-backdrop"
                role="presentation"
                onClick={() => setSeasonMovePick(null)}
              >
                <div
                  className="sm-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="sm-ep-season-move-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <header className="sm-modal-head">
                    <h2 id="sm-ep-season-move-title">Flyt episode til sæson</h2>
                    <button
                      type="button"
                      onClick={() => setSeasonMovePick(null)}
                    >
                      Luk
                    </button>
                  </header>
                  <div className="sm-settings-body">
                    <p>
                      Vælg hvilken sæson «{epTitle}» skal flyttes til. Episoden
                      lægges sidst i den valgte sæson.
                    </p>
                    <ul className="sm-ep-transfer-list">
                      {targets.map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            className="sm-ctx-menu-item"
                            onClick={() => {
                              const episodeId = seasonMovePick;
                              setSeasonMovePick(null);
                              setOpenSeasonIds((prev) => {
                                const next = new Set(prev);
                                next.add(s.id);
                                return next;
                              });
                              onMoveEpisodeToSeason(episodeId, s.id);
                            }}
                          >
                            {s.title.trim() || `Sæson ${s.number}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()
        : null}
    </div>
  );
}

function EpisodeNumberPrefix({
  episode,
  show,
  readOnly,
  onNumberChange,
}: {
  episode: Episode;
  show: boolean;
  readOnly?: boolean;
  /** Only when renaming via «Håndtér episode» — not on the open-episode link. */
  onNumberChange?: (number: string) => void;
}) {
  if (!show) return null;
  return (
    <>
      Episode{" "}
      {onNumberChange && !readOnly ? (
        <span className="sm-dash-ep-num">
          <InlineEditable
            as="span"
            className="sm-episode-num-value"
            value={String(episode.number)}
            title="Klik for at ændre episodenummer"
            onChange={(raw) => {
              const parsed = parseItemNumber(raw);
              if (parsed && parsed !== String(episode.number)) {
                onNumberChange(parsed);
              }
            }}
          />
        </span>
      ) : (
        episode.number
      )}
      {": "}
    </>
  );
}

function EpisodeRenameField({
  value,
  placeholder,
  onChange,
  onDone,
}: {
  value: string;
  placeholder: string;
  onChange: (next: string) => void;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipCommitRef = useRef(false);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  function commit() {
    if (skipCommitRef.current) return;
    const next = draft.trim() || placeholder;
    if (next !== value) onChange(next);
    onDone();
  }

  return (
    <input
      ref={inputRef}
      className="sm-inline-input sm-dash-ep-title"
      value={draft}
      aria-label="Episodenavn"
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          skipCommitRef.current = true;
          onDone();
        }
      }}
    />
  );
}
